import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, CheckCircle2, Users } from 'lucide-react';
// 👇 Edit names, locations, and messages here
const NAMES = ['Sofía', 'Camila', 'Lucía', 'Martina', 'Valentina', 'Julieta', 'Agustina', 'Paula', 'Micaela', 'Florencia', 'Catalina', 'Renata', 'Emilia', 'Antonella', 'Victoria', 'María', 'Delfina', 'Pilar', 'Bianca', 'Mía'];
const LOCATIONS = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'Tucumán', 'Mar del Plata', 'La Plata', 'Santa Fe', 'Salta', 'Neuquén'];
const MESSAGES = ['acaba de comprar hoy', 'desbloqueó su acceso hoy', 'se unió hoy a la comunidad', 'comenzó hoy su camino COSMOS', 'compró la colección hoy', 'aseguró su lugar hoy', 'recibió su acceso hace minutos'];
const ICONS = [CheckCircle2, ShoppingBag] as const;
type Notification = {
  kind: 'purchase';
  id: number;
  name: string;
  location: string;
  message: string;
  Icon: (typeof ICONS)[number];
} | {
  kind: 'counter';
  id: number;
  count: number;
};
function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function buildPurchase(): Notification {
  return {
    kind: 'purchase',
    id: Date.now() + Math.random(),
    name: pickRandom(NAMES),
    location: pickRandom(LOCATIONS),
    message: pickRandom(MESSAGES),
    Icon: pickRandom(ICONS)
  };
}
function buildCounter(count: number): Notification {
  return {
    kind: 'counter',
    id: Date.now() + Math.random(),
    count
  };
}
export function SocialProofNotifications() {
  const [current, setCurrent] = useState<Notification | null>(null);
  const [todayCount, setTodayCount] = useState<number>(() => randomBetween(118, 142));
  const [showCounterNext, setShowCounterNext] = useState(false);
  const scheduleNext = useCallback(() => {
    const delay = randomBetween(8000, 15000);
    const visibleFor = randomBetween(4500, 6000);
    const handle: {
      show: ReturnType<typeof setTimeout>;
      hide?: ReturnType<typeof setTimeout>;
    } = {
      show: setTimeout(() => {
        // Every ~3rd notification, show counter; otherwise individual
        const isCounter = showCounterNext;
        const next = isCounter ? buildCounter(todayCount) : buildPurchase();
        setCurrent(next);
        // Toggle so we alternate roughly every 2-3 notifications
        setShowCounterNext(prev => prev ? false : Math.random() < 0.4);
        handle.hide = setTimeout(() => {
          setCurrent(null);
        }, visibleFor);
      }, delay)
    };
    return handle;
  }, [showCounterNext, todayCount]);
  useEffect(() => {
    // First notification — a purchase (warm welcome before counter)
    const initial = setTimeout(() => {
      setCurrent(buildPurchase());
      setTimeout(() => setCurrent(null), randomBetween(4500, 6000));
    }, 3500);
    return () => clearTimeout(initial);
  }, []);
  useEffect(() => {
    if (current !== null) return;
    const handle = scheduleNext();
    return () => {
      clearTimeout(handle.show);
      if (handle.hide) clearTimeout(handle.hide);
    };
  }, [current, scheduleNext]);
  // Slowly bump today's count every 20–40s to feel "live"
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const wait = randomBetween(20000, 40000);
      const t = setTimeout(() => {
        setTodayCount(c => c + randomBetween(1, 3));
        tick();
      }, wait);
      return t;
    };
    const handle = tick();
    return () => {
      cancelled = true;
      if (handle) clearTimeout(handle);
    };
  }, []);
  return <div aria-live="polite" aria-atomic="true" className="fixed left-3 bottom-3 sm:left-5 sm:bottom-5 z-[60] pointer-events-none" data-id="element-232" data-mp="39dtn6vkizhsm">
      <AnimatePresence mode="wait" data-id="element-233" data-mp="3lc9s4dv7v7w">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        x: -40,
        y: 10
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0
      }} exit={{
        opacity: 0,
        x: -40,
        y: 10
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 26
      }} className="pointer-events-auto max-w-xs" data-id="element-234" data-mp="3mx721rkds9g">
            {current.kind === 'purchase' ? <div className="flex items-center gap-3 bg-white/95 backdrop-blur-md text-slate-800 rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.25)] border border-white/40 px-3 py-2.5 sm:px-4 sm:py-3" data-id="element-235" data-mp="wbuknrktz0vv">
                <div className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100" data-id="element-236" data-mp="165uqjvnxsa5n">
                  <current.Icon className="w-5 h-5" strokeWidth={2.2} data-id="element-237" data-mp="2usmn95p77h9o" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-238" data-mp="2qpqa3uifgdd4">
                  <p className="text-sm leading-tight" data-id="element-239" data-mp="2qs2oxysw27ly">
                    <span className="font-semibold text-slate-900" data-id="element-240" data-mp="2lry8u9vw7ew9">
                      {current.name}
                    </span>{' '}
                    <span className="text-slate-600" data-id="element-241" data-mp="2gtwsvxtrjvp0">
                      de {current.location}
                    </span>{' '}
                    <span className="text-slate-700" data-id="element-242" data-mp="39gpwqp2g9b38">{current.message}</span>
                  </p>
                  <div className="mt-1 flex items-center gap-1.5" data-id="element-243" data-mp="34phnbt14dpq2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-244" data-mp="34nodk4v7mpdu"></span>
                    <span className="text-[11px] text-slate-500 font-medium" data-id="element-245" data-mp="2zotud1tbyyt7">
                      Verificado · hace un momento
                    </span>
                  </div>
                </div>
              </div> : <div className="flex items-center gap-3 bg-white/95 backdrop-blur-md text-slate-800 rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.25)] border border-white/40 px-3 py-2.5 sm:px-4 sm:py-3" data-id="element-246" data-mp="1qhjmc98tz20l">
                <div className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-amber-50 text-amber-600 ring-1 ring-amber-100" data-id="element-247" data-mp="1gsrx48v5mvtd">
                  <Users className="w-5 h-5" strokeWidth={2.2} data-id="element-248" data-mp="1loys56m0imwj" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-249" data-mp="16shiqkxdvicm">
                  <p className="text-sm leading-tight" data-id="element-250" data-mp="1btf0iweheadl">
                    <span className="font-bold text-slate-900 text-base" data-id="element-251" data-mp="1bqssywrlbuvd">
                      📚 {current.count}
                    </span>{' '}
                    <span className="text-slate-700" data-id="element-252" data-mp="12rpc7zfwbrk1">
                      mujeres compraron hoy
                    </span>
                  </p>
                  <div className="mt-1 flex items-center gap-1.5" data-id="element-253" data-mp="2s7x07dz76wdi">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" data-id="element-254" data-mp="2x571przv2yhp"></span>
                    <span className="text-[11px] text-slate-500 font-medium" data-id="element-255" data-mp="37ayk6hv1vhb1">
                      Actividad de hoy · Argentina
                    </span>
                  </div>
                </div>
              </div>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}